import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Upload, FileText, Trash2, Edit2, Check, X, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    tags: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/knowledge/documents`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocuments(response.data.documents || []);
      console.log('✅ Documents loaded:', response.data.total || 0);
    } catch (error) {
      console.error('Error loading documents:', error);
      if (error.response?.status === 401) {
        toast.error('Keine Berechtigung. Bitte als Admin einloggen.');
      } else if (error.response?.status === 404) {
        toast.error('API-Endpoint nicht gefunden');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Laden der Dokumente');
      }
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Auto-fill title from filename
      const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
      setUploadForm({
        ...uploadForm,
        file,
        title: uploadForm.title || titleFromFile
      });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      toast.error('Bitte wählen Sie eine Datei und geben Sie einen Titel ein');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('tags', uploadForm.tags);

      await axios.post(`${API}/admin/knowledge/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Dokument erfolgreich hochgeladen');
      setUploadForm({ file: null, title: '', description: '', tags: '' });
      document.getElementById('file-input').value = '';
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/admin/knowledge/documents/${docId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Dokument gelöscht');
      fetchDocuments();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error(error);
    }
  };

  const handleUpdate = async (docId) => {
    try {
      await axios.patch(`${API}/admin/knowledge/documents/${docId}`, editingDoc, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Dokument aktualisiert');
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
      console.error(error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="p-6" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6" style={{ color: '#E6EDF3' }}>
          📚 RAG Dokumente verwalten
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <CardTitle style={{ color: '#7CD4FD' }}>
                  <Upload className="w-5 h-5 inline mr-2" />
                  Neues Dokument hochladen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#1EA0E610', borderColor: '#1EA0E6', border: '1px solid' }}>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#1EA0E6' }} />
                    <div>
                      <p className="text-sm font-semibold mb-1" style={{ color: '#1EA0E6' }}>
                        Zweck der RAG Knowledge Base
                      </p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>
                        Hochgeladene Dokumente werden bei Diagnosen automatisch durchsucht, 
                        um relevante Informationen aus Werkstatthandbüchern, Reparaturanleitungen 
                        oder eigenen Notizen zu finden.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#E6EDF3' }}>Datei auswählen</Label>
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    onChange={handleFileChange}
                    className="mt-2 w-full"
                    style={{ color: '#E6EDF3' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    Erlaubt: PDF, DOCX, TXT, MD (max. 50MB)
                  </p>
                </div>

                <div>
                  <Label style={{ color: '#E6EDF3' }}>Titel *</Label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    placeholder="z.B. BMW 3er Reparaturhandbuch"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <Label style={{ color: '#E6EDF3' }}>Beschreibung</Label>
                  <Textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    placeholder="Kurze Beschreibung des Inhalts..."
                    rows={3}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <Label style={{ color: '#E6EDF3' }}>Tags (kommagetrennt)</Label>
                  <Input
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                    placeholder="BMW, Werkstatthandbuch, Motor"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadForm.file || !uploadForm.title}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  {uploading ? 'Wird hochgeladen...' : 'Dokument hochladen'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Documents List */}
          <div className="lg:col-span-2">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <CardTitle style={{ color: '#7CD4FD' }}>
                  Hochgeladene Dokumente ({documents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p style={{ color: '#94A3B8' }}>Laden...</p>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
                    <p style={{ color: '#94A3B8' }}>Noch keine Dokumente hochgeladen</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                      >
                        {editingDoc?.id === doc.id ? (
                          <div className="space-y-3">
                            <Input
                              value={editingDoc.title}
                              onChange={(e) => setEditingDoc({ ...editingDoc, title: e.target.value })}
                              style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                            />
                            <Textarea
                              value={editingDoc.description || ''}
                              onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                              rows={2}
                              style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                            />
                            <Input
                              value={editingDoc.tags?.join(', ') || ''}
                              onChange={(e) => setEditingDoc({ ...editingDoc, tags: e.target.value })}
                              placeholder="Tags (kommagetrennt)"
                              style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdate(doc.id)}
                                style={{ backgroundColor: '#22C55E', color: '#fff' }}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Speichern
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => setEditingDoc(null)}
                                variant="outline"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Abbrechen
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <FileText className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                                  <h3 className="font-semibold" style={{ color: '#E6EDF3' }}>
                                    {doc.title}
                                  </h3>
                                </div>
                                {doc.description && (
                                  <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                                    {doc.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-4 text-xs" style={{ color: '#94A3B8' }}>
                                  <span>📄 {doc.file_name}</span>
                                  <span>💾 {formatFileSize(doc.file_size)}</span>
                                  <span>🕒 {new Date(doc.created_at).toLocaleDateString('de-DE')}</span>
                                </div>
                                {doc.tags && doc.tags.length > 0 && (
                                  <div className="flex gap-2 mt-2">
                                    {doc.tags.map((tag, idx) => (
                                      <span
                                        key={idx}
                                        className="px-2 py-1 rounded text-xs"
                                        style={{ backgroundColor: '#1EA0E620', color: '#1EA0E6' }}
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingDoc(doc)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(doc.id)}
                                  style={{ borderColor: '#EF4444', color: '#EF4444' }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDocuments;
